import ContractTypeMode from './ContractTypes';

/**
 * Car Allowance Options
 * Defines all available car allowance rates with their descriptions
 */
const CarAllowanceOption = {
    SUPPLY_CHAIN_MOBILE: 1,
    STANDARD: 2,
    STANDARD_GROUP_SALARY: 3,
    ROI_IRISH_ONLY: 4
};

export default CarAllowanceOption;

/**
 * Get all car allowance options with their details
 * @returns {Array} Array of car allowance option objects
 */
export function getCarAllowanceOptions() {
    return [
        {
            value: CarAllowanceOption.SUPPLY_CHAIN_MOBILE,
            name: '£200.00 p/m Supply Chain mobile',
            amount: 200.00,
            currency: 'GBP',
            description: 'Supply Chain mobile',
            isROI: false
        },
        {
            value: CarAllowanceOption.STANDARD,
            name: '£290.00 p/m Standard',
            amount: 290.00,
            currency: 'GBP',
            description: 'Standard',
            isROI: false
        },
        {
            value: CarAllowanceOption.STANDARD_GROUP_SALARY,
            name: '£330.00 p/m Standard Group Salary',
            amount: 330.00,
            currency: 'GBP',
            description: 'Standard Group Salary',
            isROI: false
        },
        {
            value: CarAllowanceOption.ROI_IRISH_ONLY,
            name: '€375.00 p/m ROI (Irish) only',
            amount: 375.00,
            currency: 'EUR',
            description: 'ROI (Irish) only',
            isROI: true
        }
    ];
}

/**
 * Get filtered car allowance options based on contract type and nationality
 * @param {number} contractType - The contract type ID
 * @param {number} nationalityId - The nationality ID of the candidate
 * @returns {Array} Filtered array of car allowance options
 */
export function getFilteredCarAllowanceOptions(contractType, nationalityId) {
    const allOptions = getCarAllowanceOptions();
    const IRISH_NATIONALITY_ID = 101;
    
    // Check if candidate is Irish or contract is ROI type
    const isIrishNational = nationalityId === IRISH_NATIONALITY_ID;
    const isROIContract = contractType === ContractTypeMode.Salaried_Operative_ROI;
    
    // Filter options based on conditions:
    // 1. Irish national (any contract) → ONLY ROI option
    // 2. ROI contract (any nationality) → ONLY ROI option
    // 3. All other cases → ONLY UK (GBP) options
    if (isIrishNational || isROIContract) {
        // Show ONLY the ROI (Irish) option: €375.00 p/m
        return allOptions.filter(option => option.isROI);
    } else {
        // Show ONLY the 3 UK (GBP) options
        return allOptions.filter(option => !option.isROI);
    }
}

/**
 * Get car allowance option by value
 * @param {number} value - The car allowance option value
 * @returns {Object|null} The car allowance option object or null
 */
export function getCarAllowanceOptionByValue(value) {
    const options = getCarAllowanceOptions();
    return options.find(option => option.value === value) || null;
}

/**
 * Get car allowance option by amount
 * @param {number} amount - The car allowance amount
 * @returns {Object|null} The car allowance option object or null
 */
export function getCarAllowanceOptionByAmount(amount) {
    const options = getCarAllowanceOptions();
    return options.find(option => option.amount === parseFloat(amount)) || null;
}

/**
 * Get car allowance option name/description
 * @param {number} value - The car allowance option value
 * @returns {string} The car allowance option name
 */
export function getCarAllowanceOptionString(value) {
    const option = getCarAllowanceOptionByValue(value);
    return option ? option.name : '';
}